import { Flex, GlobalToken } from "antd";
import { ReactNode } from "react";
import styled from "styled-components";
import { CaretRightIcon } from "@phosphor-icons/react";
import { Breadcrumb, PageTitle } from "@libertify/ui";
import bkgNoise from "@/assets/bkg-noise-grey.png";
import { pageBleedCss } from "components/layout/pagePadding";
import { useAntTheme } from "hooks/useAntTheme";
import { useBreadcrumbItems } from "hooks/useBreadcrumbItems";

export type PageLayoutProps = Readonly<{
  /** Page title (the page's h1). Also the last crumb, unless `breadcrumbLabel` is set. */
  title: string;
  /** Optional text below the title. */
  description?: ReactNode;
  /** Optional actions aligned to the right of the title (e.g. a create button). */
  actions?: ReactNode;
  /** Last crumb when it differs from the title (e.g. "Experiences" for "Experiences list"). */
  breadcrumbLabel?: string;
  className?: string;
  children: ReactNode;
}>;

/**
 * Shared layout of the app's top-level pages: the full-bleed noise background,
 * the page breadcrumb, the page title (+ description and actions) and the
 * content, full width. Spacing: title block → content marginLG; the page
 * padding comes from the app shell (components/layout/pagePadding).
 */
export const PageLayout = ({
  title,
  description,
  actions,
  breadcrumbLabel,
  className,
  children,
}: PageLayoutProps) => {
  const { theme } = useAntTheme();
  const breadcrumbItems = useBreadcrumbItems(breadcrumbLabel ?? title);

  return (
    <StyledPage $token={theme} className={className}>
      <Flex vertical gap={theme.marginLG}>
        <PageTitle
          breadcrumb={
            <Breadcrumb
              size="md"
              separator={<CaretRightIcon />}
              items={breadcrumbItems}
            />
          }
          title={title}
          description={description}
          actions={actions}
        />
        {children}
      </Flex>
    </StyledPage>
  );
};

// ─── Styled components ───

// Full-bleed background: cancel the app shell's page padding and apply it
// again inside. Positioned so pages can layer decorations over it.
const StyledPage = styled.div<{ $token: GlobalToken }>`
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  background: ${({ $token }) => $token.colorBgLayout} url(${bkgNoise}) center /
    cover no-repeat fixed;
  ${({ $token }) => pageBleedCss($token, { repad: true })}
`;
